// assets/mb-subscriptions-enhance.js
(() => {
  const cleanMoney = (str = "") =>
    str
      .replace(/\b(USD|CAD|AUD|NZD|EUR|GBP)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  const titleCaseBasic = (str = "") =>
    str
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
      .join(" ");

  const normalizeSubscribeTitle = (str = "") => {
    const s = str.replace(/\s+/g, " ").trim();
    return s
      .replace(/subscribe\s+and\s+save/i, "Subscribe & Save")
      .replace(/subscribe\s*&\s*save/i, "Subscribe & Save");
  };

  const normalizeDelivery = (str = "") => {
    const base = str.split(",")[0].trim();
    return base.replace(/^Deliver\b/i, "Delivered");
  };

  const makeCardClickable = (card, input) => {
    card.tabIndex = 0;
    card.setAttribute("role", "button");

    card.addEventListener("click", (e) => {
      if (e.target && e.target.matches('input[type="radio"]')) return;
      input?.click();
    });

    card.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        input?.click();
      }
    });
  };

  const WARN = "[mb-subs]";

  const enhanceContainer = (container) => {
    if (!container || container.dataset.mbSubsInit === "1") return;
    container.dataset.mbSubsInit = "1";

    const intro = document.querySelector(".mb-product-intro");
    if (intro) {
      const accent = getComputedStyle(intro).getPropertyValue("--mb-accent").trim();
      if (accent) container.style.setProperty("--mb-accent", accent);
    }

    const oneTimeInput = container.querySelector('input[data-radio-type="one_time_purchase"]');
    const sellingPlanInput = container.querySelector('input[data-radio-type="selling_plan"]');

    if (!oneTimeInput || !sellingPlanInput) {
      console.warn(WARN, "Radio inputs not found — using default subscription widget.");
      return;
    }

    const oneTimeCardHost = oneTimeInput.closest(".shopify_subscriptions_app_block_label")
      || oneTimeInput.closest(".subscription_group");
    const subscribeCardHost = sellingPlanInput.closest(".shopify_subscriptions_app_block_label")
      || sellingPlanInput.closest(".subscription_group");

    if (!oneTimeCardHost || !subscribeCardHost) {
      console.warn(
        WARN,
        "Card host elements not found — using default subscription widget.",
        "Tried: .shopify_subscriptions_app_block_label, .subscription_group"
      );
      return;
    }

    try {
      container.classList.add("mb-subs-enhanced");

      // Reorder: subscribe first, one-time second
      (() => {
        const parent = subscribeCardHost.parentElement;
        if (!parent) return;

        if (oneTimeCardHost.parentElement !== parent) return;

        const nodes = Array.from(parent.children);
        const subIndex = nodes.indexOf(subscribeCardHost);
        const oneIndex = nodes.indexOf(oneTimeCardHost);
        if (subIndex !== -1 && oneIndex !== -1 && subIndex < oneIndex) return;

        parent.insertBefore(subscribeCardHost, parent.firstChild);
        parent.insertBefore(oneTimeCardHost, subscribeCardHost.nextSibling);
      })();

      const ensureCard = (host, kind) => {
        host.classList.add("mb-subs-card", `mb-subs-card--${kind}`);
        let indicator = host.querySelector(".mb-subs-card__indicator");
        let content = host.querySelector(".mb-subs-card__content");

        if (!indicator) {
          indicator = document.createElement("div");
          indicator.className = "mb-subs-card__indicator";
          host.prepend(indicator);
        }

        if (!content) {
          content = document.createElement("div");
          content.className = "mb-subs-card__content";
          host.appendChild(content);
        }

        return { indicator, content };
      };

      const one = ensureCard(oneTimeCardHost, "one-time");
      const sub = ensureCard(subscribeCardHost, "subscribe");

      one.indicator.appendChild(oneTimeInput);
      sub.indicator.appendChild(sellingPlanInput);

      const oneTimeLabelText = (
        oneTimeCardHost.querySelector('.one_time_purchase_option_app_block > span')?.textContent
          || oneTimeCardHost.querySelector("label")?.textContent
          || "One-time purchase"
      ).trim();

      const subscribeHeaderText = (
        subscribeCardHost.querySelector(".group_name")?.textContent
          || subscribeCardHost.querySelector(".shopify_subscriptions_purchase_option_wrapper > label")?.textContent
          || "Subscribe and save"
      ).trim();

      const sellingPlanLabelText = (
        subscribeCardHost.querySelector(".group_list_item .title_and_price_wrapper > span")?.textContent
          || subscribeCardHost.querySelector("li label")?.textContent
          || "Deliver every month"
      ).trim();

      makeCardClickable(oneTimeCardHost, oneTimeInput);
      makeCardClickable(subscribeCardHost, sellingPlanInput);

      const render = () => {
        const onePrice = cleanMoney(oneTimeInput.getAttribute("data-variant-price") || "");
        const subPrice = cleanMoney(sellingPlanInput.getAttribute("data-variant-price") || "");
        const subCompare = cleanMoney(sellingPlanInput.getAttribute("data-variant-compare-at-price") || "");

        const oneTitle = titleCaseBasic(oneTimeLabelText);
        const subTitle = normalizeSubscribeTitle(subscribeHeaderText);
        const subSubtitle = normalizeDelivery(sellingPlanLabelText);

        one.content.innerHTML = `
          <div class="mb-subs-card__title">${oneTitle}</div>
          <div class="mb-subs-card__priceRow">
            <span class="mb-subs-card__price">${onePrice}</span>
          </div>
        `;

        sub.content.innerHTML = `
          <div class="mb-subs-card__title">${subTitle}</div>
          <div class="mb-subs-card__subtitle">${subSubtitle}</div>
          <div class="mb-subs-card__priceRow">
            <span class="mb-subs-card__price">${subPrice}</span>
            ${subCompare ? `<span class="mb-subs-card__compare">${subCompare}</span>` : ""}
          </div>
        `;

        oneTimeCardHost.classList.toggle("mb-subs-card--selected", oneTimeInput.checked);
        subscribeCardHost.classList.toggle("mb-subs-card--selected", sellingPlanInput.checked);
      };

      render();

      oneTimeInput.addEventListener("change", render);
      sellingPlanInput.addEventListener("change", render);

      const mo = new MutationObserver(() => requestAnimationFrame(render));
      mo.observe(container, {
        subtree: true,
        attributes: true,
        attributeFilter: ["data-variant-price", "data-variant-compare-at-price", "checked"],
      });

      document.addEventListener("variant:change", render);
      document.addEventListener("variant:changed", render);
      document.addEventListener("product:variant-change", render);

      const scope = container.closest(".m-main-product") || document;
      const form = scope.querySelector('product-form form[action^="/cart/add"]')
        || scope.querySelector('form[action^="/cart/add"]');

      if (form) {
        const findSellingPlanInput = () => {
          return form.querySelector('input[name="selling_plan"]')
            || (form.id ? document.querySelector(`input[name="selling_plan"][form="${form.id}"]`) : null);
        };

        const getSellingPlanId = () => {
          const sel = container.querySelector("select");
          if (sel && sel.value) return sel.value;
          return sellingPlanInput.getAttribute("data-selling-plan-id") || "";
        };

        const syncSellingPlan = () => {
          let sp = findSellingPlanInput();
          if (sellingPlanInput.checked) {
            const planId = getSellingPlanId();
            if (!sp) {
              sp = document.createElement("input");
              sp.type = "hidden";
              sp.name = "selling_plan";
              if (form.id) sp.setAttribute("form", form.id);
              form.appendChild(sp);
            }
            sp.value = planId;
            sp.disabled = false;
          } else if (sp) {
            sp.value = "";
          }
        };

        oneTimeInput.addEventListener("change", syncSellingPlan);
        sellingPlanInput.addEventListener("change", syncSellingPlan);
        syncSellingPlan();
      }
    } catch (err) {
      console.warn(WARN, "Enhancement failed — reverting to default subscription widget.", err);
      container.classList.remove("mb-subs-enhanced");
      container.dataset.mbSubsInit = "";
    }
  };

  const boot = () => {
    document
      .querySelectorAll(".shopify_subscriptions_app_container")
      .forEach(enhanceContainer);
  };

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
})();